// src/components/ProfilePhotoFramer.js
//
// "Frame your photo" — the crop step of the profile-photo flow
// (docs/ux/profile-photo.md). The photo sits behind a circular mask; what is
// inside the circle is exactly what gets saved. Drag / pinch / wheel / slider /
// arrow keys. Pan is clamped so the circle can never show empty space.
//
// Output: a 512×512 JPEG Blob handed to onSave (which uploads to Clerk). The
// sheet stays open with the crop intact if onSave rejects, so retry never
// means re-framing.

import React, { useEffect, useRef, useState, useCallback } from 'react';

const OUTPUT_PX = 512;
const MAX_ZOOM = 4;
const RING_INSET = 10; // px between stage edge and crop circle

function ProfilePhotoFramer({ src, copy, onCancel, onChooseAnother, onSave }) {
    const stageRef = useRef(null);
    const imgRef = useRef(null);         // decoded HTMLImageElement
    const pv32Ref = useRef(null);
    const pv22Ref = useRef(null);
    const pointersRef = useRef(new Map());
    const pinchDistRef = useRef(0);
    const [view, setView] = useState(null); // { base, z, ox, oy, size }
    const [decodeError, setDecodeError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    // Focus ring tracked in state, never via e.currentTarget.style (leaks across renders).
    const [stageFocused, setStageFocused] = useState(false);
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(max-width: 600px)').matches : false);

    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia('(max-width: 600px)');
        const fn = (e) => setIsMobile(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', fn); else mq.addListener(fn);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', fn); else mq.removeListener(fn); };
    }, []);

    // Clamp offsets so the crop circle is always fully covered by the image.
    const clampView = useCallback((v) => {
        const img = imgRef.current;
        if (!img || !v) return v;
        const s = v.base * v.z;
        const w = img.naturalWidth * s, h = img.naturalHeight * s;
        const c = v.size / 2, r = c - RING_INSET;
        return {
            ...v,
            ox: Math.min(c - r, Math.max(c + r - w, v.ox)),
            oy: Math.min(c - r, Math.max(c + r - h, v.oy)),
        };
    }, []);

    // Decode the chosen file and fit it to "cover" the circle, centred.
    useEffect(() => {
        setDecodeError(false); setSaveError(false); setView(null);
        imgRef.current = null;
        if (!src) return undefined;
        let cancelled = false;
        const img = new Image();
        img.onload = () => {
            if (cancelled) return;
            const size = stageRef.current ? stageRef.current.clientWidth : 280;
            const r = size / 2 - RING_INSET;
            const base = (2 * r) / Math.min(img.naturalWidth, img.naturalHeight);
            imgRef.current = img;
            setView({
                base, z: 1, size,
                ox: size / 2 - (img.naturalWidth * base) / 2,
                oy: size / 2 - (img.naturalHeight * base) / 2,
            });
            if (stageRef.current) stageRef.current.focus();
        };
        img.onerror = () => { if (!cancelled) setDecodeError(true); };
        img.src = src;
        return () => { cancelled = true; };
    }, [src]);

    // Draw the current crop into a canvas of n×n px.
    const drawCrop = useCallback((canvas, n, v) => {
        const img = imgRef.current;
        if (!canvas || !img || !v) return;
        const s = v.base * v.z, c = v.size / 2, r = c - RING_INSET;
        canvas.width = n; canvas.height = n;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, (c - r - v.ox) / s, (c - r - v.oy) / s, (2 * r) / s, (2 * r) / s, 0, 0, n, n);
    }, []);

    useEffect(() => {
        drawCrop(pv32Ref.current, 64, view);
        drawCrop(pv22Ref.current, 44, view);
    }, [view, drawCrop]);

    const zoomTo = useCallback((nz, cx, cy) => {
        setView((v) => {
            if (!v) return v;
            const z = Math.min(MAX_ZOOM, Math.max(1, nz));
            const px = cx == null ? v.size / 2 : cx;
            const py = cy == null ? v.size / 2 : cy;
            const k = z / v.z;
            return clampView({ ...v, z, ox: px - (px - v.ox) * k, oy: py - (py - v.oy) * k });
        });
    }, [clampView]);

    const panBy = useCallback((dx, dy) => {
        setView((v) => (v ? clampView({ ...v, ox: v.ox + dx, oy: v.oy + dy }) : v));
    }, [clampView]);

    // Wheel needs a non-passive listener to stop page scroll.
    useEffect(() => {
        const el = stageRef.current;
        if (!el) return undefined;
        const onWheel = (e) => {
            e.preventDefault();
            const r = el.getBoundingClientRect();
            setView((v) => {
                if (!v) return v;
                const z = Math.min(MAX_ZOOM, Math.max(1, v.z * (1 - e.deltaY * 0.0015)));
                const px = e.clientX - r.left, py = e.clientY - r.top, k = z / v.z;
                return clampView({ ...v, z, ox: px - (px - v.ox) * k, oy: py - (py - v.oy) * k });
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [clampView]);

    // Escape closes (unless mid-save).
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !saving) onCancel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onCancel, saving]);

    const onPointerDown = (e) => {
        if (!view) return;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
        pointersRef.current.set(e.pointerId, [e.clientX, e.clientY]);
    };
    const onPointerMove = (e) => {
        const pts = pointersRef.current;
        if (!pts.has(e.pointerId) || !view) return;
        const prev = pts.get(e.pointerId);
        pts.set(e.pointerId, [e.clientX, e.clientY]);
        if (pts.size >= 2) {
            const [a, b] = [...pts.values()];
            const d = Math.hypot(a[0] - b[0], a[1] - b[1]);
            if (pinchDistRef.current) {
                const r = stageRef.current.getBoundingClientRect();
                zoomTo(view.z * (d / pinchDistRef.current), (a[0] + b[0]) / 2 - r.left, (a[1] + b[1]) / 2 - r.top);
            }
            pinchDistRef.current = d;
            return;
        }
        panBy(e.clientX - prev[0], e.clientY - prev[1]);
    };
    const onPointerUp = (e) => {
        pointersRef.current.delete(e.pointerId);
        pinchDistRef.current = 0;
    };
    const onKeyDown = (e) => {
        if (!view) return;
        const step = e.shiftKey ? 32 : 8;
        const map = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
        if (map[e.key]) { e.preventDefault(); panBy(...map[e.key]); return; }
        if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomTo(view.z + 0.1); }
        if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomTo(view.z - 0.1); }
    };

    const handleSave = async () => {
        if (!view || saving) return;
        setSaving(true); setSaveError(false);
        try {
            const canvas = document.createElement('canvas');
            drawCrop(canvas, OUTPUT_PX, view);
            const blob = await new Promise((resolve, reject) => {
                canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.86);
            });
            await onSave(blob);
        } catch (e) {
            setSaveError(true);
            setSaving(false);
        }
    };

    const soft = 'color-mix(in srgb, var(--mom-charcoal) 72%, var(--mom-cream))';
    const stageSize = 'min(280px, calc(100vw - 48px))';

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
            style={{
                position: 'fixed', inset: 0, zIndex: 1200,
                background: 'rgba(40,30,30,0.45)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
            }}
        >
            <div
                role="dialog" aria-modal="true" aria-labelledby="mom-photo-frame-title"
                style={{
                    position: 'relative', background: 'var(--mom-cream)',
                    width: isMobile ? '100%' : 400, maxWidth: '100%',
                    borderRadius: isMobile ? '24px 24px 0 0' : 24,
                    padding: isMobile ? '24px 20px calc(20px + env(safe-area-inset-bottom))' : '26px 24px 22px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    animation: isMobile ? 'slideUp 0.25s ease-out' : 'modalIn 0.22s ease-out',
                }}
            >
                <button
                    type="button" aria-label="Close" disabled={saving} onClick={onCancel}
                    style={{
                        position: 'absolute', top: 12, right: 12, width: 44, height: 44, borderRadius: '50%',
                        border: 'none', background: 'transparent', cursor: saving ? 'default' : 'pointer',
                        color: soft, fontSize: 22, lineHeight: 1,
                    }}
                >×</button>
                <h2 id="mom-photo-frame-title" style={{
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 400,
                    fontSize: 22, margin: '0 0 4px', textAlign: 'center', color: 'var(--mom-charcoal)',
                }}>{copy.frameTitle}</h2>
                <p style={{ textAlign: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: soft, margin: '0 0 16px' }}>
                    {copy.frameHint}
                </p>

                <div
                    ref={stageRef}
                    tabIndex={0}
                    aria-label="Photo framing area. Arrow keys move, plus and minus zoom."
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    onKeyDown={onKeyDown}
                    style={{
                        width: stageSize, height: stageSize, margin: '0 auto', position: 'relative',
                        overflow: 'hidden', borderRadius: 18, background: '#2a2020',
                        touchAction: 'none', cursor: view ? 'grab' : 'default', outline: 'none',
                        boxShadow: stageFocused ? '0 0 0 3px color-mix(in srgb, var(--mom-primary) 45%, transparent)' : 'none',
                    }}
                    onFocus={() => setStageFocused(true)}
                    onBlur={() => setStageFocused(false)}
                >
                    {view && (
                        <img
                            src={src} alt="" draggable={false}
                            style={{
                                position: 'absolute', left: 0, top: 0, transformOrigin: '0 0',
                                transform: `translate(${view.ox}px, ${view.oy}px) scale(${view.base * view.z})`,
                                userSelect: 'none', pointerEvents: 'none', maxWidth: 'none',
                            }}
                        />
                    )}
                    {!view && !decodeError && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{
                                width: 22, height: 22, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.35)',
                                borderTopColor: '#fff', animation: 'spin 0.7s linear infinite',
                            }} />
                        </div>
                    )}
                    <div style={{
                        position: 'absolute', inset: 0, pointerEvents: 'none',
                        background: `radial-gradient(circle at center, transparent calc(50% - ${RING_INSET}px), rgba(20,12,12,0.55) calc(50% - ${RING_INSET - 1}px))`,
                    }} />
                    <div style={{
                        position: 'absolute', inset: RING_INSET, borderRadius: '50%', pointerEvents: 'none',
                        border: '2px solid rgba(255,255,255,0.9)', boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                    }} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: stageSize, margin: '14px auto 0', color: soft }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M8 11h6M20 20l-4-4" /></svg>
                    <input
                        type="range" min={1} max={MAX_ZOOM} step={0.01} aria-label="Zoom"
                        value={view ? view.z : 1} disabled={!view}
                        onChange={(e) => zoomTo(Number(e.target.value))}
                        style={{ flex: 1, height: 44, accentColor: 'var(--mom-primary)' }}
                    />
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M8 11h6M11 8v6M20 20l-4-4" /></svg>
                </div>

                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    margin: '8px 0 18px', fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: soft,
                    visibility: view ? 'visible' : 'hidden',
                }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden' }}>
                        <canvas ref={pv32Ref} style={{ width: '100%', height: '100%', display: 'block' }} />
                    </div>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', overflow: 'hidden' }}>
                        <canvas ref={pv22Ref} style={{ width: '100%', height: '100%', display: 'block' }} />
                    </div>
                    <span>{copy.previewLabel}</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button
                        type="button" onClick={handleSave} disabled={!view || saving}
                        className="mom-btn mom-btn-primary"
                        style={{
                            borderRadius: 50, padding: '14px 20px', fontSize: 14.5, fontWeight: 700,
                            opacity: !view ? 0.55 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        }}
                    >
                        {saving && <span style={{
                            width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.5)',
                            borderTopColor: '#fff', animation: 'spin 0.7s linear infinite', display: 'inline-block',
                        }} />}
                        {saving ? copy.savingLabel : copy.useLabel}
                    </button>
                    <button
                        type="button" onClick={onChooseAnother} disabled={saving}
                        className="mom-btn mom-btn-ghost"
                        style={{ borderRadius: 50, padding: '12px 20px', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)' }}
                    >{copy.chooseAnotherLabel}</button>
                </div>
                <div role="status" aria-live="polite" style={{
                    minHeight: 18, marginTop: 6, textAlign: 'center',
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: '#a3263f', lineHeight: 1.45,
                }}>
                    {decodeError ? copy.errorFormat : saveError ? copy.errorSave : ''}
                </div>
            </div>
        </div>
    );
}

export default ProfilePhotoFramer;
